import { LitElement, mailcheck } from '@hh/global.js'

class KlaviyoProfile extends LitElement {
  constructor() {
    super()
    this.form = this.querySelector('form')
    if (!this.form) return
    this.form.addEventListener('submit', this.submitForm.bind(this))

    this.klaviyoCompanyId = this.form.querySelector(
      '[name="klaviyo-company-id"]'
    ).value

    this.isSubmitted = false
    this.submitButton = this.querySelector('[type="submit"]')
    this.errorBanner = this.querySelector('.js-error-banner')

    this.firstNameInput = this.querySelector('[name="contact[first_name]"]')
    this.lastNameInput = this.querySelector('[name="contact[last_name]"]')
    this.emailInput = this.querySelector('[name="contact[email]"]')
    this.consentInput = this.querySelector('[name="contact[consent]"]')
    this.emailSuggestion = this.querySelector('.js-email-suggestion')
  }

  connectedCallback() {
    this.firstNameInput.addEventListener('input', (e) =>
      this.validateInput(this.firstNameInput, 'First Name')
    )
    this.lastNameInput.addEventListener('input', (e) =>
      this.validateInput(this.lastNameInput, 'Last Name')
    )
    this.emailInput.addEventListener('input', (e) =>
      this.validateInput(this.emailInput, 'Email')
    )
    this.consentInput.addEventListener('input', (e) =>
      this.validateInput(this.consentInput, 'Consent')
    )
  }

  disconnectedCallback() {
    this.firstNameInput.removeEventListener('input', (e) =>
      this.validateInput(this.firstNameInput, 'First Name')
    )
    this.lastNameInput.removeEventListener('input', (e) =>
      this.validateInput(this.lastNameInput, 'Last Name')
    )
    this.emailInput.removeEventListener('input', (e) =>
      this.validateInput(this.this.validateInput(this.emailInput, 'Email'))
    )
    this.consentInput.removeEventListener('input', (e) =>
      this.validateInput(this.consentInput, 'Consent')
    )
  }

  validateInput(element, field) {
    if (field === 'Consent') {
      if (element.checked) {
        element.parentNode.classList.remove('error')
      } else {
        element.parentNode.classList.add('error')
        this.errorBanner.textContent =
          "Please accept the Privacy Policy and T&C's to continue"
      }
    } else if (element.id === 'ModalForm-Email') {
      if (!element.value.trim()) {
        element.parentNode.classList.add('error')
        this.errorBanner.textContent = `${field} is required`
      } else if (!this.validateEmail(element.value)) {
        element.parentNode.classList.add('error')
        this.errorBanner.textContent = `Invalid Email`
      } else {
        element.parentNode.classList.remove('error')
      }
    } else {
      if (!element.value.trim()) {
        element.parentNode.classList.add('error')
        this.errorBanner.textContent = `${field} is required`
      } else {
        element.parentNode.classList.remove('error')
      }
    }

    this.errorCount = 0
    this.querySelectorAll(
      '.newsletter-form__input, .newsletter-form__checkbox'
    ).forEach((input) => {
      if (input.classList.contains('error')) {
        this.errorCount++
      }
    })

    if (this.errorCount === 0) {
      this.hideStatus()
    }
  }

  validateEmail = (email) => {
    const re = /\S+@\S+\.\S+/
    return re.test(email)
  }

  getBirthday = (year, month, day) => {
    const paddedMonth = month.toString().padStart(2, '0')
    const paddedDay = day.toString().padStart(2, '0')
    return `${paddedMonth}-${paddedDay}-${year}`
  }

  submitForm(event) {
    event.preventDefault()
    this.hideStatus()

    this.isSubmitted = true
    this.validateInput(this.firstNameInput, 'First Name')
    this.validateInput(this.lastNameInput, 'Last Name')
    this.validateInput(this.emailInput, 'Email')
    this.validateInput(this.consentInput, 'Consent')

    this.hasError = false
    this.errorCount = 0

    this.querySelectorAll(
      '.newsletter-form__input, .newsletter-form__checkbox'
    ).forEach((input) => {
      if (input.classList.contains('error')) {
        this.hasError = true
        this.errorCount++
      }
    })

    if (this.hasError) {
      if (this.errorCount > 1) {
        this.errorBanner.textContent = 'Please fill out all required fields'
      }
      this.showError()
      return
    }

    if (!this.klaviyoCompanyId) {
      this.adminError()
      return
    }

    this.hideStatus()

    const formData = new FormData(this.form)
    const first_name = formData.get('contact[first_name]')
    const last_name = formData.get('contact[last_name]')
    const email = formData.get('contact[email]')
    const gender = formData.get('contact[gender]')
    const phone = formData.get('contact[phone]')
    const year = formData.get('contact[year]')
    const month = formData.get('contact[month]')
    const day = formData.get('contact[day]')
    const country = formData.get('contact[country]')

    mailcheck.run({
      email,
      suggested: (suggestion) => {
        this.emailSuggestion.querySelector('.js-email').innerHTML = `${suggestion.address}@<strong>${suggestion.domain}</strong>`
        this.emailSuggestion.removeAttribute('hidden')
      },
      empty: () => {
        this.createProfile(
          first_name,
          last_name,
          gender,
          phone ? phone : '',
          email,
          country,
          this.getBirthday(year, month, day)
        )
      }
    })
  }

  async createProfile(
    first_name,
    last_name,
    gender,
    phone,
    email,
    country,
    birthday
  ) {
    this.submitButton?.setAttribute('disabled', true)

    const url = `https://a.klaviyo.com/client/profiles/?company_id=${this.klaviyoCompanyId}`

    const options = {
      method: 'POST',
      headers: {
        accept: 'application/json',
        revision: '2024-07-15',
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        data: {
          type: 'profile',
          attributes: {
            first_name,
            last_name,
            email,
            ...(phone && { phone_number: phone }),
            location: {
              country,
            },
            properties: {
              gender,
              birthday,
            },
          },
        },
      }),
    }

    try {
      const res = await fetch(url, options)
      const response = await res.json()

      if (!res.ok) {
        this.errorBanner.textContent = response?.errors?.[0]?.detail
        throw new Error(response?.errors?.[0]?.detail)
      }

      this.dispatchEvent(
        new CustomEvent('profileModalSuccess', { bubbles: true })
      )
    } catch (error) {
      this.showError()
    } finally {
      this.submitButton?.removeAttribute('disabled')
    }
  }

  adminError() {
    console.error('Klaviyo Company ID not set in theme editor settings')
    this.submitButton.setAttribute('disabled', true)
    this.showError()
  }

  showError() {
    this.errorBanner?.removeAttribute('hidden')
  }

  hideStatus() {
    // this.successBanner?.setAttribute('hidden', true)
    this.errorBanner?.setAttribute('hidden', true)
    this.emailSuggestion?.setAttribute('hidden', true)
  }
}

customElements.define('hh-klaviyo-profile', KlaviyoProfile)
